import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { POSTS } from "@/lib/content";
import { fetchDynamicPost } from "@/lib/posts";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";

function JsonLd({ data }) {
    useEffect(() => {
        if (!data) return;
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.text = JSON.stringify(data);
        document.head.appendChild(el);
        return () => el.remove();
    }, [data]);
    return null;
}

export default function NyhetDetalj() {
    const { slug } = useParams();
    const staticPost = POSTS.find((p) => p.slug === slug);
    const [dynamicPost, setDynamicPost] = useState(null);
    const [state, setState] = useState(staticPost ? "ready" : "loading");

    useEffect(() => {
        if (staticPost) return;
        let alive = true;
        setState("loading");
        fetchDynamicPost(slug)
            .then((p) => { if (alive) { setDynamicPost(p); setState("ready"); } })
            .catch(() => { if (alive) setState("missing"); });
        return () => { alive = false; };
    }, [slug, staticPost]);

    const post = staticPost || dynamicPost;
    useSEO({
        title: post?.title,
        description: post?.meta_description || post?.excerpt,
        image: post?.hero_image_url,
        type: "article",
    });

    if (state === "missing") return <Navigate to="/nyheter" replace />;
    if (state === "loading" || !post) {
        return (
            <div data-testid="nyhet-loading" className="pt-32 pb-24 text-center text-[#A1A1AA]">
                Läser in artikeln...
            </div>
        );
    }

    return (
        <article data-testid={`page-nyhet-${slug}`} className="pt-20 pb-24">
            <JsonLd data={post.json_ld} />
            <JsonLd data={post.faq_json_ld} />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <Link
                    to="/nyheter"
                    data-testid="nyhet-back"
                    className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C4A977] transition-colors"
                >
                    <ArrowLeft size={14} /> Tillbaka till nyheter
                </Link>

                <div className="mt-10 flex items-center gap-3 text-xs text-[#A1A1AA] uppercase tracking-widest">
                    <span className="text-[#C4A977]">{post.category}</span>
                    <span className="w-1 h-1 bg-white/20 rounded-full" />
                    <span>{post.date}</span>
                </div>
                <h1 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                    {post.title}
                </h1>
                <div className="hairline my-10" />

                {post.hero_image_url && (
                    <img
                        src={post.hero_image_url}
                        alt={post.hero_image_alt || post.title}
                        data-testid="nyhet-hero-image"
                        className="w-full aspect-[16/9] object-cover rounded-sm border border-white/5 mb-10"
                        loading="lazy"
                    />
                )}

                {post.content_html ? (
                    <div
                        data-testid="nyhet-html-body"
                        className="blog-html"
                        dangerouslySetInnerHTML={{ __html: post.content_html }}
                    />
                ) : (
                    <div className="prose prose-invert max-w-none">
                        {post.body.split(/\n\n+/).map((para, i) => (
                            <p
                                key={i}
                                className="text-[#F4F4F5]/90 text-lg leading-relaxed font-light mb-6 whitespace-pre-wrap"
                            >
                                {para}
                            </p>
                        ))}
                    </div>
                )}

                <div className="mt-16 pt-10 border-t border-white/10 flex items-center justify-between">
                    <Link
                        to="/kontakt"
                        data-testid="nyhet-cta-contact"
                        className="inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                    >
                        Kontakta oss gällande artikeln
                        <ArrowRight
                            size={14}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                    </Link>
                </div>
            </div>
        </article>
    );
}
